import axios from 'axios';

const api = axios.create({
  baseURL: '/keyword-intelligence/api/v1',
  headers: { 'Content-Type': 'application/json' },
  timeout: 120000
});

api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401) {
      // Distinguish JWT expiry (session ended) from domain-level auth failures
      // (e.g. third-party credential expired). Only the former should log the user out.
      const errData = error.response?.data;
      const isDomainAuthFailure = errData && (
        errData.action === 'reconnect_google' ||
        errData.error === 'credential_expired' ||
        errData.error === 'gsc_service_error'
      );

      if (!isDomainAuthFailure) {
        localStorage.removeItem('token');
        window.location.href = '/app/login';
      }
    }
    return Promise.reject(error);
  }
);

const apiService = {
  async register(payload) { return (await api.post('/auth/register', payload)).data; },
  async getCurrentUser() { return (await api.get('/auth/me')).data; },
  async listPendingUsers() { return (await api.get('/auth/admin/pending-users')).data; },
  async listAllUsers() { return (await api.get('/auth/admin/users')).data; },
  async approveUser(userId, action) { return (await api.post('/auth/admin/approve-user', { user_id: userId, action })).data; },
  async login(email, password) {
    const { data } = await api.post('/auth/login', { email, password });
    if (data.access_token) localStorage.setItem('token', data.access_token);
    return data;
  },
  logout() {
    localStorage.removeItem('token');
    window.location.href = '/app/login';
  },
  async getOAuthStatus() { return (await api.get('/auth/google/status')).data; },
  async getOAuthUrl(returnTo) {
    const qs = returnTo ? `?return_to=${encodeURIComponent(returnTo)}` : '';
    return (await api.get(`/auth/google${qs}`)).data;
  },
  async disconnectOAuth() { return (await api.post('/auth/google/disconnect')).data; },
  async listGscAccounts() { return (await api.get('/auth/gsc-accounts')).data; },
  async removeGscAccount(credentialId) { return (await api.delete(`/auth/gsc-accounts/${credentialId}`)).data; },
  async getFreshGoogleAuthUrl() { return (await api.get('/auth/google/auth-url')).data; },
  async getWebsites() { return (await api.get('/websites')).data; },
  async addWebsite(data) { return (await api.post('/websites', data)).data; },
  async deleteWebsite(id) { return (await api.delete(`/websites/${id}`)).data; },
  async startBackfill(websiteId, months = 16) { return (await api.post(`/websites/${websiteId}/backfill`, { months })).data; },
  async syncWebsite(id, days = 28) { return (await api.post(`/websites/${id}/sync`, { days })).data; },
  async getSyncStatus(id) { return (await api.get(`/websites/${id}/sync-status`)).data; },
  async getKeywordStats(websiteId, days = 28) {
    return (await api.get('/keywords/stats', { params: { website_id: websiteId, days } })).data;
  },
  async getKeywords(websiteId, page = 1, perPage = 100, sort = 'total_impressions', days = 90, bucket = '', ruleId = '', signal = true) {
    return (await api.get('/keywords', { params: { website_id: websiteId, page, per_page: perPage, sort, days, bucket, rule_id: ruleId, signal: signal ? '1' : '0' } })).data;
  },
  async getPrescriptions(websiteId) {
    return (await api.get('/brief/prescriptions', { params: { website_id: websiteId } })).data;
  },
  async getMonthlyReport(websiteId) {
    return (await api.get('/brief/monthly-report', { params: { website_id: websiteId } })).data;
  },
  async getCombinedReport(websiteId, year, month) {
    const params = { website_id: websiteId };
    if (year) params.year = year;
    if (month) params.month = month;
    return (await api.get('/brief/combined-report', { params })).data;
  },
  async saveReportNote(websiteId, year, month, workCompleted) {
    return (await api.post('/brief/report-note', {
      website_id: websiteId, year, month, work_completed: workCompleted,
    })).data;
  },
  async getFunnelReport(websiteId) {
    return (await api.get('/brief/funnel-report', { params: { website_id: websiteId } })).data;
  },
  async getBrief(websiteId, days = 28) {
    return (await api.get(`/brief/${websiteId}`, { params: { days } })).data;
  },
  async setPrescriptionStatus(prescriptionId, status, reason) {
    return (await api.post(`/brief/prescriptions/${prescriptionId}/status`, { status, reason })).data;
  },
  async getOpportunities(websiteId) {
    return (await api.get('/opportunities/quick-wins', { params: { website_id: websiteId } })).data;
  },
  async getPageAnalysis(websiteId) {
    return (await api.get('/opportunities/page-analysis', { params: { website_id: websiteId } })).data;
  },
  async getVisibilityScore(websiteId, days = 30) {
    return (await api.get('/analytics/visibility-score', { params: { website_id: websiteId, days } })).data;
  },
  async getContentGaps(websiteId) {
    return (await api.get('/analytics/content-gaps', { params: { website_id: websiteId } })).data;
  },
  async getCannibalization(websiteId) {
    return (await api.get('/analytics/cannibalization', { params: { website_id: websiteId } })).data;
  },

  async getKeywordTrends(websiteId, keyword) {
    return (await api.get('/opportunities/keyword-trends', { params: { website_id: websiteId, keyword } })).data;
  },

  async exportKeywordsCSV(websiteId) {
    const data = await api.get('/keywords', { params: { website_id: websiteId, page: 1, per_page: 9999 } });
    return data.data;
  },

  async getKeywordIntent(websiteId) {
    return (await api.get('/insights/intent', { params: { website_id: websiteId } })).data;
  },

  async getSerpFeatures(websiteId) {
    return (await api.get('/insights/serp-features', { params: { website_id: websiteId } })).data;
  },

  // Competitor Analysis Methods
  async getCompetitors(websiteId) {
    return (await api.get('/competitors/list', { params: { website_id: websiteId } })).data;
  },

  async addCompetitor(websiteId, domain, name) {
    return (await api.post('/competitors/add', { 
      website_id: websiteId, 
      competitor_domain: domain,
      competitor_name: name 
    })).data;
  },

  async removeCompetitor(competitorId) {
    return (await api.delete(`/competitors/remove/${competitorId}`)).data;
  },

  async analyzeKeywordGap(websiteId, competitorId) {
    return (await api.post('/keyword-gap/analyze', { 
      website_id: websiteId, 
      competitor_id: competitorId 
    })).data;
  },

  async getKeywordOpportunities(websiteId) {
    return (await api.get('/keyword-gap/opportunities', { params: { website_id: websiteId } })).data;
  },
  async getSiteHealth(websiteId) {
    return (await api.get('/owner/health', { params: { website_id: websiteId } })).data;
  },

  async getActionPlan(websiteId) {
    return (await api.get('/owner/action-plan', { params: { website_id: websiteId } })).data;
  },

  async getTrafficValue(websiteId) {
    return (await api.get('/owner/traffic-value', { params: { website_id: websiteId } })).data;
  },

  async getMonthOverMonth(websiteId) {
    return (await api.get('/owner/month-over-month', { params: { website_id: websiteId } })).data;
  },
  async getSearchTraffic(websiteId, days = 30) {
    return (await api.get('/traffic/search-traffic', { params: { website_id: websiteId, days } })).data;
  },

  async getGA4Traffic(websiteId) {
    return (await api.get('/traffic/ga4-traffic', { params: { website_id: websiteId } })).data;
  },
  // GA4 Integration
  async initiateGA4OAuth(websiteId) {
    return (await api.post('/ga4/connect', { website_id: websiteId })).data;
  },

  async getGA4Status(websiteId) {
    return (await api.get('/ga4/status', { params: { website_id: websiteId } })).data;
  },

  async disconnectGA4(websiteId) {
    return (await api.post('/ga4/disconnect', { website_id: websiteId })).data;
  },

  async getGA4TrafficSources(websiteId, days = 30) {
    return (await api.get('/ga4/data/traffic-sources', { params: { website_id: websiteId, days } })).data;
  },

  async getGA4DailySessions(websiteId, days = 30) {
    return (await api.get('/ga4/data/daily-sessions', { params: { website_id: websiteId, days } })).data;
  },
  async getComprehensiveGA4Analytics(websiteId, days = 30) {
    return (await api.get('/ga4/analytics/comprehensive', { params: { website_id: websiteId, days } })).data;
  },

  async getKeywordTrends(websiteId, keyword) {
    return (await api.get('/opportunities/keyword-trends', { params: { website_id: websiteId, keyword } })).data;
  },

  async exportKeywordsCSV(websiteId) {
    const data = await api.get('/keywords', { params: { website_id: websiteId, page: 1, per_page: 9999 } });
    return data.data;
  },

  async getKeywordIntent(websiteId) {
    return (await api.get('/insights/intent', { params: { website_id: websiteId } })).data;
  },

  async getSerpFeatures(websiteId) {
    return (await api.get('/insights/serp-features', { params: { website_id: websiteId } })).data;
  },

  // Competitor Analysis Methods
  async getCompetitors(websiteId) {
    return (await api.get('/competitors/list', { params: { website_id: websiteId } })).data;
  },

  async addCompetitor(websiteId, domain, name) {
    return (await api.post('/competitors/add', { 
      website_id: websiteId, 
      competitor_domain: domain,
      competitor_name: name 
    })).data;
  },

  async removeCompetitor(competitorId) {
    return (await api.delete(`/competitors/remove/${competitorId}`)).data;
  },

  async analyzeKeywordGap(websiteId, competitorId) {
    return (await api.post('/keyword-gap/analyze', { 
      website_id: websiteId, 
      competitor_id: competitorId 
    })).data;
  },

  async getKeywordOpportunities(websiteId) {
    return (await api.get('/keyword-gap/opportunities', { params: { website_id: websiteId } })).data;
  },
  async startSiteAudit(websiteId, maxPages = 500) { return (await api.post('/site-audit/start', { website_id: websiteId, max_pages: maxPages })).data; },
  async listSiteAudits(websiteId) { return (await api.get(`/site-audit/list/${websiteId}`)).data; },
  async getSiteAuditStatus(auditId) { return (await api.get(`/site-audit/status/${auditId}`)).data; },
  async getSiteAuditResults(auditId) { return (await api.get(`/site-audit/results/${auditId}`)).data; },
  async getSiteAuditPages(auditId, page = 1, perPage = 25) { return (await api.get(`/site-audit/pages/${auditId}?page=${page}&per_page=${perPage}`)).data; },
  async deleteSiteAudit(auditId) { return (await api.delete(`/site-audit/delete/${auditId}`)).data; },
};

export default apiService;
